/**
 * CR310: Hook for fetching a ribbon asset from Asset Studio
 * 
 * Resolves a ribbon asset by ID and returns the SVG content.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { AppAsset } from "@/lib/asset-studio/types";
import { getAssetImageSrc } from "@/lib/assets/getAppAssetPublicUrl";

interface UseRibbonAssetResult {
  asset: AppAsset | null;
  imageSrc: string | null;
  loading: boolean;
  error: Error | null;
  isMissing: boolean;
}

export function useRibbonAsset(assetId: string | null | undefined): UseRibbonAssetResult {
  const query = useQuery({
    queryKey: ["ribbon-asset", assetId],
    queryFn: async () => {
      if (!assetId) return null;
      
      const { data, error } = await supabase
        .from("app_assets")
        .select("*")
        .eq("id", assetId)
        .eq("type", "ribbon")
        .eq("status", "active")
        .single();
      
      if (error) {
        console.warn(`[useRibbonAsset] Asset not found: ${assetId}`, error);
        return null;
      }
      
      return data as AppAsset;
    },
    enabled: !!assetId,
    staleTime: 60_000, // Cache for 1 minute
    retry: false, // Don't retry if asset not found
  });

  const asset = query.data ?? null;
  const imageSrc = asset ? getAssetImageSrc(asset) : null;

  return {
    asset,
    imageSrc,
    loading: query.isLoading,
    error: query.error as Error | null,
    isMissing: !!assetId && !query.isLoading && !asset,
  };
}
